import React, { useState } from "react";
import { Button, Form, Row, Col, Spinner, InputGroup } from "react-bootstrap";
import type { EnrolmentFormState, EnrolmentFormLoading, RegisterBy } from "../useEnrolmentForm";

const GENDER_OPTIONS = ["Male", "Female", "Other"];

interface PhoneSearchSectionProps {
  state: EnrolmentFormState;
  loading: EnrolmentFormLoading;
  mode: "add" | "edit" | "view";
  onPhoneChange: (v: string) => void;
  onSearch: () => void;
  onShowAddProfile: (v: boolean) => void;
  onAddProfile: (registerBy: RegisterBy) => void;
  setNewParentName: (v: string) => void;
  setNewParentGender: (v: string) => void;
  setNewParentDob: (v: string) => void;
  setNewPlayerName: (v: string) => void;
  setNewPlayerGender: (v: string) => void;
  setNewPlayerDob: (v: string) => void;
  registerBy: RegisterBy | "";
}

const PhoneSearchSection: React.FC<PhoneSearchSectionProps> = ({
  state,
  loading,
  mode,
  onPhoneChange,
  onSearch,
  onShowAddProfile,
  onAddProfile,
  setNewParentName,
  setNewParentGender,
  setNewParentDob,
  setNewPlayerName,
  setNewPlayerGender,
  setNewPlayerDob,
  registerBy,
}) => {
  const isViewOrEdit = mode === "view" || mode === "edit";

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") onSearch();
  };

  return (
    <div className="enrolment-form-section">
      <h6 className="enrolment-section-title">
        <span className="enrolment-section-number">1</span>
        Player / Parent Phone Number
      </h6>

      {isViewOrEdit ? (
        /* ── View / Edit: read-only display ── */
        <div className="enrolment-field-row">
          <span className="text-muted" style={{ fontSize: 13 }}>Phone</span>
          <span className="fw-medium">
            {state.searchedProfileData?.phoneNumber
              ? `+91 ${state.searchedProfileData.phoneNumber}`
              : state.phoneNumber
              ? `+91 ${state.phoneNumber}`
              : "—"}
          </span>
          {state.searchedProfileData && (
            <span className="text-muted" style={{ fontSize: 13 }}>
              {state.searchedProfileData.fullName}
            </span>
          )}
        </div>
      ) : (
        <>
          {/* ── Phone input + Search button ── */}
          <Row className="g-2">
            <Col xs={12} md={5}>
              <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                Mobile Number
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={state.phoneNumber}
                  onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  onKeyDown={handleKeyDown}
                  maxLength={10}
                />
                <Button
                  variant="primary"
                  onClick={onSearch}
                  disabled={state.phoneNumber.length < 10 || loading.phoneSearch}
                >
                  {loading.phoneSearch ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    "Search"
                  )}
                </Button>
              </InputGroup>
            </Col>
          </Row>

          {/* ── Existing user found ── */}
          {state.phoneSearched && state.searchedProfileData && !state.showAddProfileForm && (
            <div className="enrolment-profiles-found mt-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-success" style={{ fontSize: 13 }}>
                  <i className="ri-checkbox-circle-line me-1" />
                  User found —{" "}
                  <strong>{state.searchedProfileData.fullName}</strong>
                  {" "}(+91 {state.searchedProfileData.phoneNumber})
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => onShowAddProfile(true)}
                >
                  + Add New Profile
                </Button>
              </div>
            </div>
          )}

          {/* ── User not found (404): error banner + prompt ── */}
          {state.phoneSearched && state.isNewUser && !state.showAddProfileForm && (
            <div className="mt-3">
              <div
                className="d-flex align-items-center gap-2 mb-2 px-3 py-2 rounded"
                style={{ background: "#fce4ec", border: "1px solid #f48fb1", fontSize: 13 }}
              >
                <i className="ri-user-unfollow-line text-danger" />
                <span className="text-danger fw-medium">{state.phoneSearchError}</span>
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => onShowAddProfile(true)}
              >
                + Create New Profile
              </Button>
            </div>
          )}

          {/* ── Add / Create profile sub-form ── */}
          {state.showAddProfileForm && (
            <AddProfileSubForm
              globalRegisterBy={registerBy}
              allowSelf={state.isNewUser}
              state={state}
              loading={loading}
              setNewParentName={setNewParentName}
              setNewParentGender={setNewParentGender}
              setNewParentDob={setNewParentDob}
              setNewPlayerName={setNewPlayerName}
              setNewPlayerGender={setNewPlayerGender}
              setNewPlayerDob={setNewPlayerDob}
              onSubmit={onAddProfile}
              onCancel={() => onShowAddProfile(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

// ── Add-Profile sub-form ─────────────────────────────────────────────────────
// Has its own local registerBy radio so the user doesn't need to fill Section 2
// before the "Create Profile" button becomes active.

interface AddProfileSubFormProps {
  /** registerBy already chosen in Section 2 (may be empty string) */
  globalRegisterBy: RegisterBy | "";
  /** When true (new user / 404), show both Self and Parent options.
   *  When false (existing user), only show Parent since a self profile already exists. */
  allowSelf: boolean;
  state: EnrolmentFormState;
  loading: EnrolmentFormLoading;
  setNewParentName: (v: string) => void;
  setNewParentGender: (v: string) => void;
  setNewParentDob: (v: string) => void;
  setNewPlayerName: (v: string) => void;
  setNewPlayerGender: (v: string) => void;
  setNewPlayerDob: (v: string) => void;
  onSubmit: (rb: RegisterBy) => void;
  onCancel: () => void;
}

const AddProfileSubForm: React.FC<AddProfileSubFormProps> = ({
  globalRegisterBy,
  allowSelf,
  state,
  loading,
  setNewParentName,
  setNewParentGender,
  setNewParentDob,
  setNewPlayerName,
  setNewPlayerGender,
  setNewPlayerDob,
  onSubmit,
  onCancel,
}) => {
  // Local registerBy so the form is self-contained.
  // When allowSelf is false (self profile already exists), force "parent" so both parent
  // and child fields are always shown — regardless of globalRegisterBy being "self".
  const [localRegisterBy, setLocalRegisterBy] = useState<RegisterBy | "">(
    !allowSelf ? "parent" : (globalRegisterBy || "")
  );

  const isParent = localRegisterBy === "parent";

  const parentFieldsFilled =
    !!state.newParentName.trim() && !!state.newParentGender;

  const playerFieldsFilled =
    !isParent ||
    (!!state.newPlayerName.trim() && !!state.newPlayerGender);

  const canSubmit = !!localRegisterBy && parentFieldsFilled && playerFieldsFilled;

  return (
    <div className="enrolment-add-profile-form mt-3 p-3 border rounded">
      <p className="fw-medium mb-3" style={{ fontSize: 14 }}>
        {isParent ? "New Parent + Player Details" : "New Profile Details"}
      </p>

      {/* ── Registering As (local) — only show when both options are valid ── */}
      {allowSelf ? (
        <>
          <Form.Label className="mb-1 text-muted d-block" style={{ fontSize: 12 }}>
            Registering As *
          </Form.Label>
          <div className="d-flex gap-3 mb-3">
            {(["self", "parent"] as RegisterBy[]).map((rb) => (
              <Form.Check
                key={rb}
                type="radio"
                id={`add-profile-rb-${rb}`}
                label={rb === "self" ? "Self" : "Registering a child"}
                checked={localRegisterBy === rb}
                onChange={() => setLocalRegisterBy(rb)}
              />
            ))}
          </div>
        </>
      ) : (
        // Existing user — self profile already exists, only adding a child profile
        <p className="text-muted mb-3" style={{ fontSize: 12 }}>
          <i className="ri-information-line me-1" />
          A self profile already exists for this number. You can add a child profile below.
        </p>
      )}

      {/* ── Parent / self fields ── */}
      <Row className="g-2 mb-2">
        <Col xs={12} md={4}>
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
            {isParent ? "Parent Name" : "Full Name"} *
          </Form.Label>
          <Form.Control
            placeholder={isParent ? "Parent full name" : "Full name"}
            value={state.newParentName}
            onChange={(e) => setNewParentName(e.target.value)}
          />
        </Col>
        <Col xs={6} md={2}>
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>Gender *</Form.Label>
          <Form.Select
            value={state.newParentGender}
            onChange={(e) => setNewParentGender(e.target.value)}
          >
            <option value="">Select</option>
            {GENDER_OPTIONS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
            Date of Birth
          </Form.Label>
          <Form.Control
            type="date"
            value={state.newParentDob}
            onChange={(e) => setNewParentDob(e.target.value)}
          />
        </Col>
      </Row>

      {/* ── Player (child) fields — only when localRegisterBy === "parent" ── */}
      {isParent && (
        <>
          <hr className="my-2" />
          <p className="fw-medium mb-2" style={{ fontSize: 13 }}>Player (Child) Details</p>
          <Row className="g-2 mb-3">
            <Col xs={12} md={4}>
              <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                Player Name *
              </Form.Label>
              <Form.Control
                placeholder="Player full name"
                value={state.newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>Gender *</Form.Label>
              <Form.Select
                value={state.newPlayerGender}
                onChange={(e) => setNewPlayerGender(e.target.value)}
              >
                <option value="">Select</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                Date of Birth
              </Form.Label>
              <Form.Control
                type="date"
                value={state.newPlayerDob}
                onChange={(e) => setNewPlayerDob(e.target.value)}
              />
            </Col>
          </Row>
        </>
      )}

      <div className="d-flex gap-2 mt-2">
        <Button
          variant="primary"
          size="sm"
          disabled={!canSubmit || loading.addProfile}
          onClick={() => localRegisterBy && onSubmit(localRegisterBy)}
        >
          {loading.addProfile ? (
            <Spinner size="sm" animation="border" />
          ) : (
            "Create Profile"
          )}
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default PhoneSearchSection;
